/**
 * Weekly review — the Reports tab that walks the week in five steps.
 *
 * Nothing here is new data. Every figure already lives somewhere else in the
 * app (Time, Reports, Clients, the task registers); the review's only job is
 * to put them in an order you can work through on a Friday afternoon and tick
 * off: what did I log, what is owed a bill, what budget is running hot, what
 * slipped, what is coming.
 *
 * SELF-CONTAINED ON PURPOSE. It is an experiment, so it touches the rest of the
 * app in three places — the tab in views/Reports.tsx, the /review redirect in
 * App.tsx and the switch in Settings — all keyed off `useWeeklyReview`.
 * Turning it off in Settings hides the tab; deleting it is this file plus
 * those three.
 *
 * It renders inside Reports, so it publishes no header of its own: the week
 * and the progress live in the picker row instead.
 *
 * Ticks are per week and kept in this browser only: they are a checklist for
 * the sitting, not a record anyone else needs.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { card, Dot, ProgressBar } from "@/components/atoms";
import { itemProjectId } from "@/components/ItemRow";
import { Registers } from "@/components/Registers";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useAllItems, useProjects } from "@/hooks/useData";
import { resolveClient, useClients, useTimeEntries } from "@/hooks/useTime";
import { itemsById } from "@/lib/deps";
import { isOverdue, parseDate, scheduledFor, shortDate } from "@/lib/format";
import {
  breakdownText, budgetStatus, clock, entryDayKey, hours, inBounds, isUnbilled, money, periodBounds,
  toIsoLocal, totalsOf,
} from "@/lib/timeRollup";
import type { Item, Project } from "@/lib/types";
import { InvoiceExport } from "@/views/reports/InvoiceExport";

type StepKey = "time" | "bill" | "budgets" | "loose" | "next";

/**
 * Friday to Sunday you are reviewing the week you are in; Monday to Thursday,
 * the one just gone. That is when people actually sit down to do this.
 */
function defaultOffset(now = new Date()): number {
  const dow = now.getDay();
  return dow === 5 || dow === 6 || dow === 0 ? 0 : -1;
}

function loadTicks(weekKey: string): StepKey[] {
  try { return JSON.parse(localStorage.getItem(`ph.review.${weekKey}`) || "[]"); } catch { return []; }
}
function saveTicks(weekKey: string, ticks: StepKey[]): void {
  try { localStorage.setItem(`ph.review.${weekKey}`, JSON.stringify(ticks)); } catch { /* private mode */ }
}

export function WeeklyReview() {
  const navigate = useNavigate();
  const { openItem } = useTaskEditor();
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();

  const [offset, setOffset] = useState(defaultOffset);
  const [invoicing, setInvoicing] = useState(false);
  const week = useMemo(() => periodBounds("week", offset), [offset]);
  const following = useMemo(() => periodBounds("week", offset + 1), [offset]);
  const weekKey = toIsoLocal(week.start);

  const [ticksByWeek, setTicksByWeek] = useState<Record<string, StepKey[]>>({});
  const ticks = ticksByWeek[weekKey] ?? loadTicks(weekKey);
  function toggle(step: StepKey) {
    const next = ticks.includes(step) ? ticks.filter((t) => t !== step) : [...ticks, step];
    saveTicks(weekKey, next);
    setTicksByWeek((m) => ({ ...m, [weekKey]: next }));
  }

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const byId = useMemo(() => itemsById(items), [items]);
  const open = useMemo(() => items.filter((i) => i.status !== "done"), [items]);

  /* -- 1. Time ------------------------------------------------------------- */
  const weekEntries = useMemo(() => entries.filter((e) => inBounds(e, week)), [entries, week]);
  const weekTotals = totalsOf(weekEntries, projects);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(week.start);
    d.setDate(week.start.getDate() + i);
    const key = toIsoLocal(d);
    const seconds = weekEntries.filter((e) => entryDayKey(e) === key).reduce((n, e) => n + (e.durationSeconds || 0), 0);
    return { key, date: d, seconds, weekday: i < 5, future: d.getTime() > Date.now() };
  }), [week, weekEntries]);
  const maxDay = Math.max(1, ...days.map((d) => d.seconds));
  const emptyWorkdays = days.filter((d) => d.weekday && !d.future && d.seconds === 0);

  /* -- 2. Bill ------------------------------------------------------------- */
  // All unbilled work, not just this week's: last month's hours that never went
  // out are exactly what a review exists to catch.
  const owed = useMemo(() => {
    const rows = new Map<string, { name: string; seconds: number; amount: number; currency: string; oldest: string }>();
    for (const e of entries.filter((e) => isUnbilled(e, projects))) {
      const client = clients.find((c) => c.id === e.client) ?? resolveClient(projectById.get(e.project ?? ""), clients);
      const key = client?.id ?? "__none";
      const row = rows.get(key) ?? { name: client?.name ?? "No client", seconds: 0, amount: 0, currency: e.currency || "USD", oldest: e.start };
      row.seconds += e.durationSeconds || 0;
      row.amount += ((e.durationSeconds || 0) / 3600) * (e.rateSnapshot ?? 0);
      if (e.start < row.oldest) row.oldest = e.start;
      rows.set(key, row);
    }
    return [...rows.values()].sort((a, b) => b.amount - a.amount || b.seconds - a.seconds);
  }, [entries, clients, projectById]);

  /* -- 3. Budgets ---------------------------------------------------------- */
  const budgets = useMemo(() => projects
    .filter((p) => p.status !== "archived")
    .map((p) => ({ project: p, status: budgetStatus(p, entries) }))
    .filter((r): r is { project: Project; status: NonNullable<ReturnType<typeof budgetStatus>> } => !!r.status)
    .sort((a, b) => b.status.ratio - a.status.ratio), [projects, entries]);
  const hot = budgets.filter((b) => b.status.ratio >= 0.75);

  /* -- 4. Loose ends ------------------------------------------------------- */
  const overdue = useMemo(() => open
    .filter(isOverdue)
    .sort((a, b) => (scheduledFor(a) ?? "").localeCompare(scheduledFor(b) ?? "")), [open]);

  /* -- 5. Next week -------------------------------------------------------- */
  const inFollowing = (s?: string) => {
    const d = parseDate(s);
    return !!d && d >= following.start && d <= following.end;
  };
  const coming = useMemo(() => open
    .filter((i) => inFollowing(i.start) || inFollowing(i.due))
    .sort((a, b) => (scheduledFor(a) ?? "").localeCompare(scheduledFor(b) ?? "")),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [open, following]);
  const comingByProject = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const i of coming) {
      const pid = itemProjectId(i) ?? "";
      map.set(pid, [...(map.get(pid) ?? []), i]);
    }
    return [...map];
  }, [coming]);

  const doneCount = ticks.length;

  return (
    <div style={{ maxWidth: 920, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* -- Week picker -------------------------------------------------------- */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => setOffset((o) => o - 1)} aria-label="Previous week" style={pager}>‹</button>
        <div className="mono" style={{
          height: 36, minWidth: 170, padding: "0 12px", borderRadius: 8, fontSize: 13,
          display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line2)",
        }}>{week.label}</div>
        <button onClick={() => setOffset((o) => o + 1)} aria-label="Next week" style={pager}>›</button>
        {offset !== defaultOffset() && (
          <button onClick={() => setOffset(defaultOffset())} style={{ ...ghost, marginLeft: 4 }}>Back to review week</button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>
          {doneCount} of 5 steps done
        </span>
        <span style={{ width: 140 }}>
          <ProgressBar value={(doneCount / 5) * 100} color={doneCount === 5 ? "var(--good)" : "var(--accent)"} height={6} />
        </span>
      </div>

      {/* -- 1 ------------------------------------------------------------------ */}
      <Step n={1} title="Check your time" done={ticks.includes("time")} onToggle={() => toggle("time")}
        summary={`${clock(weekTotals.seconds)} logged${breakdownText(weekTotals, clock) ? ` · ${breakdownText(weekTotals, clock)}` : ""}`}
        action={<button onClick={() => navigate("/time?tab=entries")} style={ghost}>Open entries</button>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, alignItems: "end" }}>
          {days.map((d) => {
            const gap = d.weekday && !d.future && d.seconds === 0;
            return (
              <div key={d.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <span className="mono" style={{ fontSize: 11.5, color: gap ? "var(--warn)" : "var(--ink3)" }}>
                  {d.seconds ? clock(d.seconds) : gap ? "none" : "—"}
                </span>
                <div style={{ width: "100%", height: 56, display: "flex", alignItems: "flex-end", background: "var(--tint)", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ width: "100%", height: `${(d.seconds / maxDay) * 100}%`, background: "var(--accent)", opacity: d.weekday ? 1 : 0.5 }} />
                </div>
                <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
                  {d.date.toLocaleDateString(undefined, { weekday: "short" })}
                </span>
              </div>
            );
          })}
        </div>
        {emptyWorkdays.length > 0 && (
          <Note tone="warn">
            Nothing logged on {emptyWorkdays.map((d) => d.date.toLocaleDateString(undefined, { weekday: "long" })).join(", ")}.
            If you worked, now is the time to add it — it only gets harder to remember.
          </Note>
        )}
      </Step>

      {/* -- 2 ------------------------------------------------------------------ */}
      <Step n={2} title="Bill what's owed" done={ticks.includes("bill")} onToggle={() => toggle("bill")}
        summary={owed.length ? `${owed.length} ${owed.length === 1 ? "client" : "clients"} with unbilled time` : "Everything billable is invoiced"}
        action={owed.length ? <button onClick={() => setInvoicing(true)} style={ghost}>Invoice attachment</button> : undefined}>
        {owed.length === 0 ? (
          <Empty>No billable time is waiting on an invoice.</Empty>
        ) : (
          <Table>
            {owed.map((r) => (
              <Row key={r.name}
                left={r.name}
                meta={`oldest ${new Date(r.oldest).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
                right={`${hours(r.seconds)} h${r.amount > 0 ? ` · ${money(r.amount, r.currency)}` : ""}`} />
            ))}
          </Table>
        )}
      </Step>

      {/* -- 3 ------------------------------------------------------------------ */}
      <Step n={3} title="Watch the budgets" done={ticks.includes("budgets")} onToggle={() => toggle("budgets")}
        summary={budgets.length === 0 ? "No projects have a budget set" : hot.length ? `${hot.length} at 75% or more` : "All comfortably under"}>
        {hot.length === 0 ? (
          <Empty>{budgets.length === 0 ? "Set a fixed-fee or retainer budget on a project to see it here." : "Nothing is past three-quarters of its budget."}</Empty>
        ) : (
          <Table>
            {hot.map(({ project, status }) => (
              <div key={project.id} style={{ padding: "9px 0", borderTop: "1px solid var(--tint2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <Dot color={project.color} />
                  <button onClick={() => navigate(`/project/${project.id}`)} style={linkBtn}>{project.name}</button>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>{status.kind === "retainer" ? status.window : "fixed fee"}</span>
                  <span className="mono" style={{ marginLeft: "auto", fontSize: 12.5, color: status.ratio > 1 ? "var(--bad)" : "var(--ink2)" }}>
                    {hours(status.usedSeconds)} / {status.budgetHours} h
                  </span>
                </div>
                <ProgressBar value={Math.min(100, status.ratio * 100)} color={status.ratio > 1 ? "var(--bad)" : status.ratio >= 0.9 ? "var(--warn)" : "var(--accent)"} />
              </div>
            ))}
          </Table>
        )}
      </Step>

      {/* -- 4 ------------------------------------------------------------------ */}
      <Step n={4} title="Clear loose ends" done={ticks.includes("loose")} onToggle={() => toggle("loose")}
        summary={overdue.length ? `${overdue.length} overdue` : "Nothing overdue"}>
        {overdue.length > 0 && (
          <Table>
            {overdue.slice(0, 8).map((i) => (
              <TaskLine key={i.id} item={i} project={projectById.get(itemProjectId(i) ?? "")} onOpen={() => openItem(i)} />
            ))}
            {overdue.length > 8 && (
              <button onClick={() => navigate("/overdue")} style={{ ...linkBtn, fontSize: 12.5, padding: "8px 0" }}>
                See all {overdue.length} overdue ›
              </button>
            )}
          </Table>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: overdue.length ? 12 : 0 }}>
          <Registers open={open} byId={byId} projectById={projectById} />
        </div>
      </Step>

      {/* -- 5 ------------------------------------------------------------------ */}
      <Step n={5} title="Look at the week ahead" done={ticks.includes("next")} onToggle={() => toggle("next")}
        summary={`${following.label} · ${coming.length} ${coming.length === 1 ? "task" : "tasks"} dated`}>
        {coming.length === 0 ? (
          <Empty>Nothing is scheduled or due that week. Plan it in Todoist and sync.</Empty>
        ) : (
          comingByProject.map(([pid, rows]) => {
            const project = projectById.get(pid);
            return (
              <div key={pid} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "var(--ink3)", margin: "6px 0 2px" }}>
                  <Dot color={project?.color ?? "var(--muted3)"} />{project?.name ?? "No project"}
                </div>
                {rows.map((i) => <TaskLine key={i.id} item={i} onOpen={() => openItem(i)} highlightDeadline={inFollowing(i.due)} />)}
              </div>
            );
          })
        )}
      </Step>

      {doneCount === 5 && (
        <div style={{ ...card, padding: "16px 18px", textAlign: "center", fontSize: 13.5, color: "var(--goodInk)", background: "var(--goodTint)" }}>
          Review done for {week.label}.
        </div>
      )}

      {invoicing && <InvoiceExport onClose={() => setInvoicing(false)} />}
    </div>
  );
}

/* -- Parts ------------------------------------------------------------------ */

function Step({ n, title, summary, done, onToggle, action, children }: {
  n: number; title: string; summary: string; done: boolean; onToggle: () => void;
  action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section style={{ ...card, padding: "16px 18px", opacity: done ? 0.72 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button
          onClick={onToggle}
          aria-pressed={done}
          aria-label={done ? `Mark step ${n} not done` : `Mark step ${n} done`}
          style={{
            width: 26, height: 26, flex: "none", borderRadius: "50%", cursor: "pointer",
            border: done ? "none" : "1.5px solid var(--line2)",
            background: done ? "var(--good)" : "var(--surface)",
            color: done ? "var(--onAccent)" : "var(--muted)", fontSize: 12, fontWeight: 600,
          }}
        >{done ? "✓" : n}</button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{summary}</div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function TaskLine({ item, project, onOpen, highlightDeadline }: {
  item: Item; project?: Project; onOpen: () => void; highlightDeadline?: boolean;
}) {
  const when = scheduledFor(item);
  return (
    <button onClick={onOpen} className="row-hover" style={{
      display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
      padding: "7px 0", border: "none", borderTop: "1px solid var(--tint2)", background: "none", cursor: "pointer",
    }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.title}
      </span>
      {project && <span style={{ flex: "none", fontSize: 12, color: "var(--muted)" }}>{project.name}</span>}
      {highlightDeadline && item.due && (
        <span style={{ flex: "none", fontSize: 11, padding: "2px 7px", borderRadius: 999, background: "var(--badTint)", color: "var(--bad)" }}>
          deadline {shortDate(item.due)}
        </span>
      )}
      {when && <span className="mono" style={{ flex: "none", fontSize: 12, color: "var(--muted)", width: 52, textAlign: "right" }}>{shortDate(when)}</span>}
    </button>
  );
}

function Table({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>;
}

function Row({ left, meta, right }: { left: string; meta?: string; right: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "8px 0", borderTop: "1px solid var(--tint2)" }}>
      <span style={{ fontSize: 13.5, color: "var(--ink)" }}>{left}</span>
      {meta && <span style={{ fontSize: 12, color: "var(--muted)" }}>{meta}</span>}
      <span className="mono" style={{ marginLeft: "auto", fontSize: 13, color: "var(--ink2)" }}>{right}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12.5, color: "var(--muted2)" }}>{children}</div>;
}

function Note({ tone, children }: { tone: "warn"; children: React.ReactNode }) {
  return (
    <div style={{
      marginTop: 12, fontSize: 12.5, lineHeight: 1.45, padding: "9px 11px", borderRadius: 8,
      background: tone === "warn" ? "var(--warnTint)" : "var(--tint)", color: "var(--ink2)",
    }}>{children}</div>
  );
}

const pager: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 8, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 15, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};
const ghost: React.CSSProperties = {
  padding: "7px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
};
const linkBtn: React.CSSProperties = {
  border: "none", background: "none", padding: 0, cursor: "pointer",
  fontSize: 13.5, color: "var(--ink)", textAlign: "left",
};
